import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { X, Loader } from "lucide-react";

const OtpVerificationModal = ({ show, handleClose, handleVerify, email }) => {
    const [otp, setOtp] = useState(["", "", "", "", "", ""]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [errors, setErrors] = useState({});
    const [timeLeft, setTimeLeft] = useState(2 * 60);
    const [resending, setResending] = useState(false);
    const [resendAvailable, setResendAvailable] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        if (!show) return;

        if (timeLeft <= 0) {
            setResendAvailable(true);
            return;
        }

        const timer = setInterval(() => {
            setTimeLeft((prev) => prev - 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [timeLeft, show]);

    const formatTime = (seconds) => {
        const m = String(Math.floor(seconds / 60)).padStart(2, "0");
        const s = String(seconds % 60).padStart(2, "0");
        return `${m}:${s}`;
    };

    const handleChange = (value, index) => {
        if (/^[0-9]?$/.test(value)) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            if (value && index < 5) {
                document.getElementById(`otp-input-${index + 1}`).focus();
            }
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            document.getElementById(`otp-input-${index - 1}`).focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");
        setErrors({});

        const newOtp = otp.join("");

        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;

            const response = await fetch(`${apiUrl}/verify-email`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    email: email?.trim().toLowerCase(),
                    otp_code: String(newOtp),
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                if (data.errors) {
                    setErrors(data.errors);
                    setMessage(data.message || "Validation failed");
                } else {
                    setMessage(data.message || "Something went wrong");
                }
                return;
            }

            setMessage("OTP Verified Successfully!");
            navigate("/");
            handleClose();
        } catch (err) {
            setMessage("An unexpected error occurred. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResendAvailable(false);
        setResending(true);
        setMessage("");
        try {
            const apiUrl = process.env.REACT_APP_BASE_URL;

            const response = await fetch(`${apiUrl}/resend-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                },
                body: JSON.stringify({
                    email: email?.trim().toLowerCase(),
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                setMessage(data.message || "Failed to resend OTP");
                return;
            }

            setMessage("A new OTP has been sent to your email.");
            setTimeLeft(2 * 60);
        } catch (err) {
            setMessage("Could not resend OTP. Please try again.");
        } finally {
            setResending(false);
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative animate-fade-in-up">

                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-100">
                    <h3 className="text-xl font-bold text-gray-900">Verify OTP</h3>
                    <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                        <X size={24} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    <p className="text-gray-500 mb-6 text-center">Enter the 6-digit code sent to your email</p>

                    <form onSubmit={handleSubmit}>
                        <div
                            className="flex justify-between mb-8 gap-2"
                            onPaste={(e) => {
                                e.preventDefault();
                                const pasteData = e.clipboardData.getData("text").trim();
                                if (!/^\d+$/.test(pasteData)) return;

                                const newOtp = [...otp];
                                pasteData.split("").slice(0, otp.length).forEach((char, i) => {
                                    newOtp[i] = char;
                                });
                                setOtp(newOtp);

                                const lastIndex = Math.min(newOtp.length - 1, pasteData.length - 1);
                                document.getElementById(`otp-input-${lastIndex}`)?.focus();
                            }}
                        >
                            {otp.map((digit, index) => (
                                <input
                                    key={index}
                                    id={`otp-input-${index}`}
                                    type="text"
                                    value={digit}
                                    maxLength="1"
                                    onChange={(e) => handleChange(e.target.value, index)}
                                    onKeyDown={(e) => handleKeyDown(e, index)}
                                    className="w-12 h-12 text-center text-xl font-bold border-2 border-gray-200 rounded-lg focus:border-brand-red focus:ring-4 focus:ring-brand-red0 outline-none transition-all"
                                />
                            ))}
                        </div>

                        {message && (
                            <div className={`p-3 rounded-lg text-center text-sm font-medium mb-4 ${message.includes("Success") || message.includes("new OTP") ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                                {message}
                            </div>
                        )}

                        <div className="text-center mb-6">
                            {resendAvailable ? (
                                <button
                                    type="button"
                                    onClick={handleResend}
                                    disabled={resending}
                                    className="text-brand-red font-semibold hover:underline bg-transparent border-0 p-0"
                                >
                                    {resending ? "Sending..." : "Resend OTP"}
                                </button>
                            ) : (
                                <span className="text-gray-400 font-medium">
                                    Time remaining: {formatTime(timeLeft)}
                                </span>
                            )}
                        </div>

                        {!resendAvailable && (
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full bg-brand-red hover:bg-red-700 text-white font-bold py-3 rounded-xl transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 flex justify-center items-center"
                            >
                                {loading ? (
                                    <>
                                        <Loader className="animate-spin mr-2 h-5 w-5" />
                                        Verifying...
                                    </>
                                ) : (
                                    "Verify"
                                )}
                            </button>
                        )}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default OtpVerificationModal;
